import { z } from "zod";
import { costEstimateEnvelopeSchema, costEstimateOverviewSchema, damageAppraisalEnvelopeSchema, damageAppraisalOverviewSchema, parseCostEstimateIncidentOverview, parseDamageAppraisalIncidentOverview, projectCostEstimateOverview, projectDamageAppraisalOverview, } from "./costEstimateDamageAppraisal.js";
import { invoiceEnvelopeSchema, invoiceOverviewSchema, parseInvoiceIncidentOverview, projectInvoiceOverview } from "./invoice.js";
export * from "./contract.js";
export * from "./costEstimateDamageAppraisal.js";
export * from "./invoice.js";
export * from "./personOverview.js";
export * from "./shared.js";
export * from "./view.js";
// The fixed-shape discriminated union is the internal parse target feeding the canonical
// projection; it stays exported for the schema-degradation tests, but app code consumes only
// the `OverviewView` that `parseIncidentOverview` returns.
export const incidentOverviewSchema = z.discriminatedUnion("workflow", [
    costEstimateOverviewSchema,
    damageAppraisalOverviewSchema,
    invoiceOverviewSchema,
]);
// Loose envelope: validates only each workflow's partitioning. Container and document shapes are
// checked per-entry by the workflow-specific parse functions, so one structurally-drifted
// container can't reject the whole response.
const incidentOverviewEnvelopeSchema = z.discriminatedUnion("workflow", [
    costEstimateEnvelopeSchema,
    damageAppraisalEnvelopeSchema,
    invoiceEnvelopeSchema,
]);
/**
 * Resilient counterpart to `incidentOverviewSchema.parse`, projected to the canonical view: the
 * envelope's partitioning must be intact, but each container is validated independently so a
 * single drifted container or document degrades to a per-container load error instead of failing
 * the whole overview. `expectedWorkflow` is asserted (throws on mismatch); the returned
 * `OverviewView` is workflow-agnostic.
 */
export function parseIncidentOverview(raw, expectedWorkflow) {
    const envelope = incidentOverviewEnvelopeSchema.parse(raw);
    if (envelope.workflow !== expectedWorkflow) {
        throw new Error(`Expected workflow "${expectedWorkflow}" but received "${envelope.workflow}"`);
    }
    switch (envelope.workflow) {
        case "cost-estimate":
            return projectCostEstimateOverview(parseCostEstimateIncidentOverview(envelope));
        case "damage-appraisal":
            return projectDamageAppraisalOverview(parseDamageAppraisalIncidentOverview(envelope));
        case "invoice":
            return projectInvoiceOverview(parseInvoiceIncidentOverview(envelope));
        default:
            // Compile-time exhaustiveness: TS errors here if the workflow union grows without a case.
            envelope;
            throw new Error(`Unhandled workflow: ${envelope.workflow}`);
    }
}
